import { useEffect, useState } from "react";
import { api } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import KawasanFormDialog from "@/components/KawasanFormDialog";
import KawasanDetailDialog from "@/components/KawasanDetailDialog";
import MapDialog from "@/components/MapDialog";
import { Plus, Map, LandPlot, Trees, Building2, Home, Pencil, Trash2, Loader2, FileSpreadsheet, FileText, TrendingUp, MapPin, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import BulkActionBar, { useRowSelection } from "@/components/BulkActionBar";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

// Konstanta modul agar referensi objek stabil antar render (hindari re-render chart)
const CHART_MARGIN = { left: -20, right: 10 };
const TICK_STYLE = { fontSize: 11, fill: "#64748B" };
const LEGEND_STYLE = { fontSize: 11 };

export const KONDISI_META = {
  "BAIK": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "RUSAK RINGAN": "bg-amber-50 text-amber-700 border-amber-200",
  "RUSAK BERAT": "bg-rose-50 text-rose-700 border-rose-200",
};

export const fmtLuas = (v) =>
  `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} m²`;

const StatBox = ({ icon: Icon, label, value, tone, testid }) => (
  <Card className="p-5 border-slate-200 shadow-none rounded-lg" data-testid={testid}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">{label}</p>
        <p className="text-xl sm:text-2xl font-black tracking-tight text-slate-900 mt-1.5 truncate">{value}</p>
      </div>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${tone}`}>
        <Icon className="h-4.5 w-4.5" />
      </div>
    </div>
  </Card>
);

export default function AreaView() {
  const [stats, setStats] = useState(null);
  const [kawasanList, setKawasanList] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [tren, setTren] = useState([]);
  const [mapTarget, setMapTarget] = useState(null);
  const [search, setSearch] = useState("");
  const { selected, toggle, toggleAll, clear } = useRowSelection();

  const bulkDelete = async () => {
    for (const id of selected) {
      await api.delete(`/kawasan/${id}`).catch(() => {});
    }
    toast.success(`${selected.size} kawasan dihapus`);
    clear();
    load();
  };

  const exportExcel = async (ids = "") => {
    setExporting(true);
    try {
      const res = await api.get("/area/export/excel", { params: ids ? { ids } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = ids ? "area_pengelolaan_terpilih.xlsx" : "area_pengelolaan.xlsx";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Laporan area pengelolaan diunduh");
    } catch {
      toast.error("Gagal mengekspor laporan");
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async (ids = "") => {
    setExportingPdf(true);
    try {
      const res = await api.get("/area/export/pdf", { params: ids ? { ids } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = ids ? "laporan_area_pengelolaan_terpilih.pdf" : "laporan_area_pengelolaan.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Laporan PDF area diunduh");
    } catch {
      toast.error("Gagal mengekspor laporan PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const load = () => {
    api.get("/area/stats").then((res) => setStats(res.data)).catch(() => {});
    api.get("/kawasan").then((res) => setKawasanList(res.data)).catch(() => setKawasanList([]));
    api.get("/area/kondisi-tren").then((res) => setTren(res.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (payload) => {
    if (editing) {
      await api.put(`/kawasan/${editing.id}`, payload);
      toast.success("Kawasan diperbarui");
    } else {
      await api.post("/kawasan", payload);
      toast.success("Kawasan baru ditambahkan");
    }
    setFormOpen(false);
    setEditing(null);
    load();
  };

  const handleDelete = async () => {
    await api.delete(`/kawasan/${deleteTarget.id}`);
    toast.success("Kawasan dihapus");
    setDeleteTarget(null);
    load();
  };

  const kawasanFiltered = (kawasanList || []).filter((k) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return k.nama.toLowerCase().includes(q) || (k.kondisi || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-5" data-testid="area-view">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <StatBox icon={LandPlot} label="Luas Indoor" value={fmtLuas(stats?.total_luas_indoor)}
          tone="bg-blue-50 text-blue-600" testid="area-stat-indoor" />
        <StatBox icon={Trees} label="Luas Outdoor" value={fmtLuas(stats?.total_luas_outdoor)}
          tone="bg-emerald-50 text-emerald-600" testid="area-stat-outdoor" />
        <StatBox icon={Map} label="Jumlah Kawasan" value={stats?.jumlah_kawasan ?? 0}
          tone="bg-violet-50 text-violet-600" testid="area-stat-kawasan" />
        <StatBox icon={Building2} label="Jumlah Gedung" value={stats?.jumlah_gedung ?? 0}
          tone="bg-amber-50 text-amber-600" testid="area-stat-gedung" />
        <StatBox icon={Home} label="Rumah Dinas" value={stats?.jumlah_rumah_dinas ?? 0}
          tone="bg-rose-50 text-rose-600" testid="area-stat-rumah-dinas" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="area-kondisi-summary">
          <span className="font-semibold text-slate-600">Kondisi aset:</span>
          {(stats?.kondisi_aset || []).length ? stats.kondisi_aset.map((k) => (
            <Badge key={k.name} variant="outline" className={`${KONDISI_META[k.name] || ""} font-medium`}>
              {k.name}: {k.value}
            </Badge>
          )) : <span className="text-slate-400">belum ada aset</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari kawasan..." data-testid="search-kawasan-input"
              className="h-10 pl-9 w-full sm:w-64" />
          </div>
          <Button variant="outline" onClick={exportExcel} disabled={exporting} data-testid="export-area-button"
            className="gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-md transition-colors">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Ekspor Excel
          </Button>
          <Button variant="outline" onClick={exportPdf} disabled={exportingPdf} data-testid="export-area-pdf-button"
            className="gap-2 border-rose-200 text-rose-700 hover:bg-rose-50 rounded-md transition-colors">
            {exportingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            Laporan PDF
          </Button>
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-kawasan-button"
            className="gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors">
            <Plus className="h-4 w-4" /> Tambah Kawasan
          </Button>
        </div>
      </div>

      {kawasanList === null ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : kawasanList.length === 0 ? (
        <Card className="p-12 border-slate-200 shadow-none rounded-lg text-center text-slate-400" data-testid="kawasan-empty">
          <Map className="h-8 w-8 mx-auto mb-3 text-slate-300" />
          Belum ada kawasan. Klik "Tambah Kawasan" untuk mulai mencatat area pengelolaan.
        </Card>
      ) : kawasanFiltered.length === 0 ? (
        <Card className="p-10 border-slate-200 shadow-none rounded-lg text-center text-slate-400" data-testid="kawasan-no-result">
          <Search className="h-7 w-7 mx-auto mb-3 text-slate-300" />
          Tidak ada kawasan yang cocok dengan "{search}".
        </Card>
      ) : (
        <>
        <BulkActionBar count={selected.size} onClear={clear} entity="kawasan"
          onDelete={bulkDelete}
          onExportExcel={() => exportExcel([...selected].join(","))}
          onExportPdf={() => exportPdf([...selected].join(","))} />
        <Card className="border-slate-200 shadow-none rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kawasan-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  <th className="px-4 py-3 w-8">
                    <Checkbox checked={kawasanFiltered.length > 0 && selected.size >= kawasanFiltered.length}
                      onCheckedChange={() => toggleAll(kawasanFiltered.map((k) => k.id))} data-testid="kawasan-select-all" />
                  </th>
                  {["Kawasan", "Luas Indoor", "Luas Outdoor", "Gedung", "Rumah Dinas", "Kondisi", "Aksi"].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {kawasanFiltered.map((k) => (
                  <tr key={k.id} onClick={() => setDetailTarget(k)} data-testid={`kawasan-row-${k.id}`}
                    className="border-b border-slate-100 last:border-0 cursor-pointer hover:bg-blue-50/40 transition-colors">
                    <td className="px-4 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.has(k.id)} onCheckedChange={() => toggle(k.id)}
                        data-testid={`kawasan-select-${k.id}`} />
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">{k.nama}</td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{fmtLuas(k.luas_indoor)}</td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{fmtLuas(k.luas_outdoor)}</td>
                    <td className="px-4 py-3 text-slate-700">{k.jumlah_gedung}</td>
                    <td className="px-4 py-3 text-slate-700">{k.jumlah_rumah_dinas}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`${KONDISI_META[k.kondisi] || ""} font-medium`}>{k.kondisi}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={(e) => { e.stopPropagation(); setMapTarget(k); }}
                          data-testid={`map-kawasan-${k.id}`} title="Lihat peta kawasan"
                          className="h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 transition-colors">
                          <MapPin className="h-4 w-4" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setEditing(k); setFormOpen(true); }}
                          data-testid={`edit-kawasan-${k.id}`}
                          className="h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(k); }}
                          data-testid={`delete-kawasan-${k.id}`}
                          className="h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        </>
      )}

      <KawasanFormDialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) setEditing(null); }}
        kawasan={editing} onSave={handleSave} />

      {tren.some((t) => t.BAIK + t["RUSAK RINGAN"] + t["RUSAK BERAT"] > 0) && (
        <Card className="p-6 border-slate-200 shadow-none rounded-lg" data-testid="kondisi-tren-card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-teal-600" />
            <h3 className="text-base font-semibold text-slate-900">Tren Kondisi Aset (12 Bulan Terakhir)</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={tren} margin={CHART_MARGIN}>
              <CartesianGrid vertical={false} stroke="#F1F5F9" />
              <XAxis dataKey="bulan" tick={TICK_STYLE} />
              <YAxis allowDecimals={false} tick={TICK_STYLE} />
              <Tooltip />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Area type="monotone" dataKey="BAIK" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.55} />
              <Area type="monotone" dataKey="RUSAK RINGAN" stackId="1" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.55} />
              <Area type="monotone" dataKey="RUSAK BERAT" stackId="1" stroke="#E11D48" fill="#E11D48" fillOpacity={0.55} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      <KawasanDetailDialog open={!!detailTarget} onOpenChange={(v) => !v && setDetailTarget(null)}
        kawasan={detailTarget} onChanged={load} />

      <MapDialog open={!!mapTarget} onOpenChange={(v) => !v && setMapTarget(null)}
        worker={mapTarget ? { nama: mapTarget.nama, link_maps: mapTarget.link_maps, lokasi_kerja: mapTarget.nama, alamat: "" } : null} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-kawasan-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Kawasan?</AlertDialogTitle>
            <AlertDialogDescription>
              Kawasan "{deleteTarget?.nama}" beserta seluruh gedung dan rumah dinas di dalamnya akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-kawasan">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-kawasan"
              className="bg-rose-600 hover:bg-rose-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
